import { ROUTES } from '@/constants/routes';
import { useAdminPreferencesStore } from '@/store/admin-preferences.store';
import { cn } from '@/utils/cn';
import {
  LayoutDashboard,
  Users,
  Building2,
  Globe,
  Cpu,
  Vault,
  Receipt,
  Megaphone,
  LifeBuoy,
  Activity,
  Settings,
  UserCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, href: ROUTES.DASHBOARD },
    ],
  },
  {
    title: 'GESTION',
    items: [
      { label: 'Utilisateurs', icon: Users, href: ROUTES.USERS },
      { label: 'Organisations', icon: Building2, href: ROUTES.ORGANISATIONS },
      { label: 'Domaines', icon: Globe, href: ROUTES.DOMAINS },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Automatisations', icon: Cpu, href: ROUTES.AUTOMATIONS },
      { label: 'Coffres-forts', icon: Vault, href: ROUTES.VAULTS },
    ],
  },
  {
    title: 'FINANCES',
    items: [
      { label: 'Facturation', icon: Receipt, href: ROUTES.BILLING },
    ],
  },
  {
    title: 'COMMUNICATION',
    items: [
      { label: 'Communications', icon: Megaphone, href: ROUTES.COMMUNICATIONS },
      { label: 'Support', icon: LifeBuoy, href: ROUTES.SUPPORT },
    ],
  },
  {
    title: 'TECHNIQUE',
    items: [
      { label: 'Monitoring', icon: Activity, href: ROUTES.MONITORING },
    ],
  },
];

const bottomNav: NavItem[] = [
  { label: 'Configuration', icon: Settings, href: ROUTES.SETTINGS },
  { label: 'Mon profil', icon: UserCircle, href: ROUTES.PROFILE },
];

interface SidebarProps {
  onClose?: () => void;
  isMobile?: boolean;
}

export function Sidebar({ onClose, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useAdminPreferencesStore();
  const collapsed = !isMobile && sidebarCollapsed;

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-neutral-900 border-r border-neutral-800 transition-all duration-200',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header / Logo */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-neutral-800">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Logo width={100} height={36} className="brightness-0 invert" />
            <span className="rounded-md bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-400">Admin</span>
          </div>
        )}
        {collapsed && (
          <Logo width={28} height={28} className="mx-auto brightness-0 invert" />
        )}
        {isMobile && onClose && (
          <button onClick={onClose} className="rounded-lg p-1.5 text-neutral-500 transition-all duration-200 hover:bg-neutral-800 hover:text-neutral-300">
            <X className="h-5 w-5" />
          </button>
        )}
        {!isMobile && !collapsed && (
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-neutral-500 transition-all duration-200 hover:bg-neutral-800 hover:text-neutral-300"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {!isMobile && collapsed && (
          <button
            onClick={toggleSidebar}
            className="mx-auto mt-2 rounded-lg p-1.5 text-neutral-500 transition-all duration-200 hover:bg-neutral-800 hover:text-neutral-300"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <div className="space-y-6">
          {navigation.map((section) => (
            <div key={section.title || 'main'}>
              {section.title && !collapsed && (
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                  {section.title}
                </p>
              )}
              {section.title && collapsed && (
                <div className="mx-auto mb-2 h-px w-6 bg-neutral-800" />
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={isMobile ? onClose : undefined}
                      className={cn(
                        'flex items-center gap-3 rounded-xl p-3 text-sm font-medium transition-all duration-200',
                        active
                          ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-lg shadow-brand-500/25'
                          : 'text-neutral-400 hover:bg-neutral-800 hover:text-white',
                        collapsed && 'justify-center px-0'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className={cn('h-5 w-5 shrink-0', active ? 'text-white' : '')} />
                      {!collapsed && item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Bottom nav */}
      <div className="border-t border-neutral-800 px-2 py-3 space-y-1">
        {bottomNav.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={isMobile ? onClose : undefined}
              className={cn(
                'flex items-center gap-3 rounded-xl p-3 text-sm font-medium transition-all duration-200',
                active
                  ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-lg shadow-brand-500/25'
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white',
                collapsed && 'justify-center px-0'
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon className={cn('h-5 w-5 shrink-0', active ? 'text-white' : '')} />
              {!collapsed && item.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
